import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import SafeIcon from '../common/SafeIcon';
import * as LuIcons from 'react-icons/lu';

const { LuCheck, LuCar, LuAnchor, LuTruck, LuCaravan } = LuIcons;

const ServicesSection = () => {
  const { t } = useTranslation();

  const serviceCategories = [
    {
      key: 'car',
      icon: LuCar,
      image: 'https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvanBlZw==/6530e3b138b49251336ed444fdbb572d.jpg',
      position: 'center 40%'
    },
    {
      key: 'marine',
      icon: LuAnchor,
      image: 'https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvanBlZw==/9dd632bfe7fee91b0315766dcc5ec2b1.jpg',
      position: 'center 60%'
    },
    {
      key: 'motorhome',
      icon: LuTruck,
      image: 'https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvanBlZw==/27d63c35222354b60fe20f6cea890fbe.jpg',
      position: 'center center'
    },
    {
      key: 'trailer',
      icon: LuCaravan,
      image: 'https://media-manager-c.questera.ai/greta-media/25fe57c277fc4d49baa2815a1646d92e367cb2ce89a7d2c278a4f8202228d92c658a999133b0fffe7649687a42276768/images/aW1hZ2UvanBlZw==/82946504fa89716adac7ac657c834698.jpg',
      position: 'center 30%'
    }
  ];

  return (
    <section id="services" className="section-container bg-opacity-50">
      <div className="section-header">
        <h2 className="section-title">{t('services.title')}</h2>
        <p className="section-subtitle">{t('services.subtitle')}</p>
      </div>

      <div className="services-grid">
        {serviceCategories.map((cat, i) => (
          <motion.div 
            key={cat.key} 
            className="service-card"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            whileHover="hover"
          >
            <motion.div 
              className="card-bg"
              variants={{
                hover: { scale: cat.key === 'motorhome' ? 1.2 : 1.1 }
              }}
              transition={{ duration: 0.6, ease: "easeOut" }}
              style={{ 
                backgroundImage: `url(${cat.image})`,
                backgroundPosition: cat.position,
                scale: cat.key === 'motorhome' ? 1.1 : 1
              }}
            />
            
            <div className="service-card-overlay">
              <div className="cat-icon-container">
                <SafeIcon icon={cat.icon} />
              </div>
              
              <h3>{t(`services.${cat.key}.title`)}</h3>
              
              <ul className="service-list">
                {t(`services.${cat.key}.items`, { returnObjects: true }).map((item, idx) => (
                  <li key={idx}>
                    <SafeIcon icon={LuCheck} className="check-icon" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        ))}
      </div>

      <style jsx>{`
        .section-header {
          text-align: center;
          margin-bottom: 5rem;
        }

        .section-title {
          font-size: 3rem;
          font-weight: 800;
          margin-bottom: 1rem;
        }

        .section-subtitle {
          color: #848489;
          font-size: 1.1rem;
        }

        .services-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
          gap: 2rem;
          max-width: 1300px;
          margin: 0 auto;
        }

        .service-card {
          position: relative;
          height: 100%;
          min-height: 480px;
          border-radius: 24px;
          overflow: hidden;
          border: 1px solid rgba(221, 219, 230, 0.15);
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
          background: #07070E;
        }

        .card-bg {
          position: absolute;
          inset: 0;
          background-size: cover;
          z-index: 0;
          transition: 0.6s ease-out;
        }

        .service-card-overlay {
          position: relative;
          z-index: 1;
          background: linear-gradient(
            to bottom, 
            rgba(7, 7, 14, 0.4) 0%, 
            rgba(7, 7, 14, 0.8) 40%, 
            rgba(7, 7, 14, 0.98) 100%
          );
          padding: 2.5rem;
          height: 100%;
          display: flex;
          flex-direction: column;
          gap: 1.5rem;
          backdrop-filter: blur(1px);
          transition: 0.4s ease;
        }

        .service-card:hover .service-card-overlay {
          background: linear-gradient(
            to bottom, 
            rgba(7, 7, 14, 0.2) 0%, 
            rgba(7, 7, 14, 0.7) 40%, 
            rgba(7, 7, 14, 0.95) 100%
          );
          backdrop-filter: blur(0px);
        }

        .cat-icon-container {
          font-size: 2.5rem;
          margin-bottom: 0.5rem;
          width: 70px;
          height: 70px;
          background: rgba(131, 119, 190, 0.8);
          border-radius: 18px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 20px rgba(0, 0, 0, 0.3);
          transition: 0.3s ease;
        }

        .service-card:hover .cat-icon-container {
          background: var(--primary-gradient);
          transform: scale(1.05) translateY(-5px);
        }

        .service-card h3 {
          font-size: 1.75rem;
          font-weight: 700;
          color: white;
          text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
        }

        .service-list {
          list-style: none;
          display: flex;
          flex-direction: column;
          gap: 0.8rem;
          margin-top: auto;
        }

        .service-list li {
          display: flex;
          gap: 0.75rem;
          align-items: flex-start;
          color: #DDDBE6;
          font-size: 1rem;
          text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
        }

        .check-icon {
          color: #8377BE;
          flex-shrink: 0;
          margin-top: 4px;
          filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.8));
        }

        @media (max-width: 768px) {
          .section-title { font-size: 2.2rem; }
          .section-header { margin-bottom: 3rem; }
          .services-grid { gap: 1.5rem; }
          .service-card { min-height: 420px; }
          .service-card-overlay { padding: 1.75rem; }
        }

        @media (max-width: 480px) {
          .section-title { font-size: 1.9rem; }
          .services-grid { grid-template-columns: 1fr; }
          .service-card-overlay { padding: 1.5rem; gap: 1rem; }
          .cat-icon-container { width: 56px; height: 56px; border-radius: 14px; }
          .service-card h3 { font-size: 1.4rem; }
          .service-list li { font-size: 0.9rem; }
        }
      `}</style>
    </section>
  );
};

export default ServicesSection;